import type { ReactElement } from 'react';

import { LegalDoc } from '@/widgets/legal-doc';
import { COMPANY } from '@/shared/config';

const EFFECTIVE = '2026년 9월 11일';

/**
 * 개인정보처리방침 — 실제로 수집하는 항목 기준(14 §5.3).
 *
 * 화면에서 받는 것과 방침에 적힌 것이 어긋나면 동의 자체가 무효가 될 수 있다.
 * 그래서 항목을 **가입 / 소셜 / 온보딩 / 문의 / 설문 / 자동수집** 여섯 갈래로 나눠 그대로 적었다.
 * 온보딩·설문 항목을 바꿀 때는 이 문서의 「처리하는 개인정보 항목」도 같이 고친다.
 *
 * ⚠️ **공개 전에 반드시 정리해야 하는 것** — 읽는 사람에게 알릴 내용이 아니라 우리 숙제라
 * 화면에 띄우지 않고 여기 남긴다.
 *
 * 1. **Gmail SMTP 로는 수신거부 링크를 자동으로 붙일 수 없다.** 광고성 정보 수신 동의 문서가
 *    「메일 하단 수신거부 링크」를 약속하므로, 링크와 `/unsubscribe` 화면을 직접 만들어야
 *    정보통신망법 제50조를 지킬 수 있다.
 * 2. **온보딩 항목의 필수·선택 구분**이 화면과 어긋난다. 온보딩에 건너뛰기가 없어 세 항목을
 *    모두 받는데, 같은 법 제16조 제2항은 필수 최소 정보가 아닌 것의 수집을 이유로 서비스 제공을
 *    거부하지 못하게 한다. 건너뛸 수 있게 하거나, 받아야 하는 이유를 정리해야 한다.
 * 3. 법률 검토를 받지 않았다.
 */
export function PrivacyPage(): ReactElement {
  return (
    <LegalDoc
      title="개인정보처리방침"
      effectiveDate={EFFECTIVE}
      articles={[
        {
          id: 'purpose',
          title: '개인정보의 처리 목적',
          body: (
            <>
              <p>{COMPANY.legalName}는 다음의 목적으로 개인정보를 처리합니다.</p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>회원 식별 및 로그인</li>
                <li>자료 제공 및 다운로드 이력 관리</li>
                <li>교육 문의 응대 및 제안</li>
                <li>설문 참여자에 대한 자료 발송 및 무료 컨설팅 추첨 안내</li>
                <li>동의한 이용자에 한한 광고성 정보 전송</li>
                <li>서비스 이용 통계 분석 및 개선</li>
              </ul>
              <p>
                수집한 개인정보는 위 목적 외의 용도로 이용하지 않으며, 목적이 변경될 경우 별도의
                동의를 받습니다.
              </p>
            </>
          ),
        },
        {
          id: 'items',
          title: '처리하는 개인정보 항목',
          body: (
            <>
              <p>
                <strong>회원 가입 (필수)</strong> — 이메일 주소, 비밀번호
              </p>
              <p>
                <strong>소셜 로그인 시</strong> — 카카오·네이버로부터 제공받는 계정 식별자 및 이메일
                주소
              </p>
              <p>
                <strong>가입 후 온보딩</strong> — 닉네임, 직무, 가장 반복적으로 시간이 드는 업무,
                자동화 경험 수준
              </p>
              <p>
                <strong>교육 문의 시</strong> — 담당자명, 이메일, 연락처, 회사·기관명, 문의 유형,
                예상 인원, 희망 시기, 예산 범위, 문의 내용
              </p>
              <p>
                <strong>설문 참여 시</strong> — 이메일, 설문 응답(하는 일, 시간이 많이 드는 업무,
                사용하는 업무 툴, AI 활용 경험, 배우고 싶은 자동화 사례와 이유, 자유 서술 내용),
                무료 컨설팅 추첨 응모 여부
              </p>
              <p>
                <strong>자동으로 수집되는 정보</strong> — 접속 IP 주소, 접속 일시, 브라우저·기기
                정보, 서비스 이용 기록, 유입 경로
              </p>
            </>
          ),
        },
        {
          id: 'retention',
          title: '개인정보의 보유·이용 기간',
          body: (
            <>
              <p>
                원칙적으로 <strong>회원 탈퇴 시 지체 없이 파기</strong>합니다. 다만 다음의 경우에는
                해당 기간 동안 보관합니다.
              </p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>교육 문의 정보 — 문의 처리 완료 후 1년</li>
                <li>설문 응답 — 수집일로부터 1년</li>
                <li>광고성 정보 수신 동의·철회 기록 — 동의 철회 후 6개월 (분쟁 대응 목적)</li>
                <li>서비스 이용 기록(접속 로그) — 3개월 (통신비밀보호법)</li>
                <li>부정 이용 기록 — 1년</li>
              </ul>
              <p>관계 법령에서 더 긴 보존 의무를 정한 경우에는 그 기간을 따릅니다.</p>
            </>
          ),
        },
        {
          id: 'age',
          title: '만 14세 미만 아동의 개인정보',
          body: (
            <p>
              회사는 <strong>만 14세 미만 아동의 개인정보를 수집하지 않습니다.</strong> 회원 가입 시
              만 14세 이상임을 확인하며, 만 14세 미만임이 확인되면 해당 정보를 지체 없이 파기합니다.
            </p>
          ),
        },
        {
          id: 'thirdparty',
          title: '개인정보의 제3자 제공',
          body: (
            <>
              <p>
                회사는 이용자의 개인정보를 <strong>제3자에게 제공하지 않습니다.</strong>
              </p>
              <p>
                다만 법령에 따라 수사기관 등이 적법한 절차로 요청하는 경우에는 그에 따를 수
                있습니다.
              </p>
            </>
          ),
        },
        {
          id: 'outsourcing',
          title: '개인정보 처리의 위탁',
          body: (
            <>
              <p>회사는 서비스 제공을 위해 아래 업무를 외부에 위탁하고 있습니다.</p>
              <dl className="flex flex-col gap-3">
                {(
                  [
                    [
                      '서비스 인프라 운영',
                      'Amazon Web Services, Inc.',
                      '서버·데이터베이스·파일 저장소 운영',
                    ],
                    ['이메일 발송', 'Google LLC (Gmail SMTP)', '자료·안내 메일 전송'],
                    ['이용 통계 분석', 'Google LLC (Google Analytics)', '서비스 이용 통계 분석'],
                    [
                      '오류 모니터링',
                      'Functional Software, Inc. (Sentry)',
                      '장애 원인 파악 및 복구',
                    ],
                  ] as const
                ).map(([work, vendor, purpose]) => (
                  <div key={work} className="flex flex-col gap-0.5">
                    <dt className="font-medium text-foreground">{work}</dt>
                    <dd>
                      수탁자 — {vendor} · 위탁 업무 — {purpose}
                    </dd>
                  </div>
                ))}
              </dl>
              <p>
                위탁 계약 시 개인정보 보호 관련 사항을 명시하고, 수탁자가 안전하게 처리하는지
                관리·감독합니다. 수탁자가 바뀌면 이 방침에 반영하고 변경 사항을 공지합니다.
              </p>
            </>
          ),
        },
        {
          id: 'overseas',
          title: '개인정보의 국외 이전',
          body: (
            <>
              <p>
                위 수탁자는 <strong>미국</strong>에 소재한 사업자입니다. 서비스 이용 과정에서 아래와
                같이 개인정보가 국외로 이전됩니다.
              </p>
              <p>
                다만 서버·데이터베이스·파일은 Amazon Web Services 의{' '}
                <strong>서울 리전(대한민국)</strong>에 보관합니다.
              </p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>이전되는 항목 — 이메일 주소, 서비스 이용 기록, 접속 IP 주소</li>
                <li>이전 국가 — 미국</li>
                <li>이전 시기와 방법 — 서비스 이용 시점에 네트워크를 통해 전송</li>
                <li>이전받는 자 — Google LLC, Functional Software, Inc.</li>
                <li>이용 목적과 보유 기간 — 위 「개인정보 처리의 위탁」 항목과 같습니다</li>
              </ul>
              <p>
                이전을 거부하실 수 있습니다. 다만 이전되는 정보가 서비스 제공에 필요한 정보이므로,
                거부하시면 회원 가입과 자료 다운로드를 이용하실 수 없습니다. 거부는 {COMPANY.email}{' '}
                로 알려주시면 처리합니다.
              </p>
            </>
          ),
        },
        {
          id: 'rights',
          title: '정보주체의 권리와 행사 방법',
          body: (
            <>
              <p>이용자는 언제든지 다음 권리를 행사할 수 있습니다.</p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>개인정보 열람 요구</li>
                <li>오류가 있을 경우 정정 요구</li>
                <li>삭제 요구</li>
                <li>처리 정지 요구</li>
              </ul>
              <p>
                마이페이지 &gt; 수신 설정에서 광고성 정보 수신 동의를 직접 변경할 수 있습니다. 그
                밖의 요청은 {COMPANY.email} 로 보내주시면 지체 없이 처리합니다.
              </p>
            </>
          ),
        },
        {
          id: 'destruction',
          title: '개인정보의 파기',
          body: (
            <>
              <p>
                보유 기간이 지나거나 처리 목적이 달성되면 지체 없이 파기합니다. 전자적 파일은 복구할
                수 없는 방법으로 삭제하고, 출력물은 분쇄하거나 소각합니다.
              </p>
            </>
          ),
        },
        {
          id: 'cookie',
          title: '쿠키의 사용',
          body: (
            <>
              <p>
                회사는 로그인 상태 유지와 이용 통계 분석을 위해 쿠키를 사용합니다. 이용자는 브라우저
                설정에서 쿠키 저장을 거부할 수 있습니다.
              </p>
              <p>쿠키를 거부하면 로그인이 필요한 기능의 이용이 제한될 수 있습니다.</p>
            </>
          ),
        },
        {
          id: 'security',
          title: '개인정보의 안전성 확보 조치',
          body: (
            <ul className="flex list-disc flex-col gap-1 pl-5">
              <li>개인정보 접근 권한의 최소화 및 접근 기록 보관</li>
              <li>전송 구간 암호화(HTTPS) 적용</li>
              <li>비밀번호 등 인증 정보의 암호화 저장</li>
              <li>접속 기록의 위·변조 방지</li>
            </ul>
          ),
        },
        {
          id: 'officer',
          title: '개인정보 보호책임자',
          body: (
            <>
              <dl className="flex flex-col gap-1">
                {(
                  [
                    ['성명', COMPANY.privacyOfficer.name],
                    ['직책', COMPANY.privacyOfficer.title],
                    ['연락처', COMPANY.privacyOfficer.phone],
                    ['이메일', COMPANY.privacyOfficer.email],
                  ] as const
                ).map(([label, value]) => (
                  <div key={label} className="flex gap-3">
                    <dt className="w-16 shrink-0 text-foreground/70">{label}</dt>
                    <dd>{value}</dd>
                  </div>
                ))}
              </dl>
              <p>
                개인정보 침해에 대한 신고·상담이 필요하면 개인정보침해신고센터(privacy.kisa.or.kr,
                국번 없이 118), 대검찰청 사이버수사과, 경찰청 사이버수사국에 문의할 수 있습니다.
              </p>
            </>
          ),
        },
        {
          id: 'change',
          title: '방침의 변경',
          body: (
            <p>
              이 방침을 변경할 때는 적용일과 변경 사유를 명시해 적용일 <strong>7일 전부터</strong>{' '}
              공지합니다. 이용자의 권리에 중대한 영향을 주는 변경은 <strong>30일 전부터</strong>{' '}
              공지합니다.
            </p>
          ),
        },
      ]}
    />
  );
}
